import {
  SPEED_TEST_PATH_LABELS,
  describeSpeedTestPath,
  formatBitRate,
  formatTime,
  type SpeedTest,
} from '@simpleacs/shared';

/**
 * What the Line test card says once a run settles (operator, 2026-10-12:
 * "clicking test seems to give a success since there's no error, but we need
 * a confirmation after clicking test to make sure it was successful").
 *
 * The card used to start the run with a toast that read like a success
 * ("Asked … to measure its line") and then change its figures silently, and
 * the tray closed a FAILED run the same way it closed a finished one. Now the
 * outcome is said in so many words, on the card, until it is dismissed: what
 * happened, when (EAT), what was measured, and over which path.
 */

export interface LineTestConfirmation {
  tone: 'success' | 'warn' | 'danger';
  title: string;
  detail: string;
}

function latency(run: SpeedTest): string {
  return run.latencyMs === null ? 'latency not measured' : `${run.latencyMs.toFixed(run.latencyMs < 10 ? 1 : 0)} ms`;
}

function where(run: SpeedTest): string {
  return run.path === 'unknown' ? describeSpeedTestPath('unknown') : `over the ${SPEED_TEST_PATH_LABELS[run.path]}`;
}

/** The one-line outcome — the tray's done text and the failure it throws. */
export function describeLineTestOutcome(run: SpeedTest): string {
  if (run.status === 'done') {
    const figures = `${formatBitRate(run.downBps)} down, ${formatBitRate(run.upBps)} up, ${latency(run)}`;
    return run.error ? `${figures}, ${where(run)} — ${run.error}` : `${figures}, ${where(run)}.`;
  }
  return `${run.status === 'timed_out' ? 'Timed out' : 'Failed'}: ${run.error ?? 'the CPE gave no reason'}`;
}

export function lineTestConfirmation(run: SpeedTest): LineTestConfirmation {
  const at = `${formatTime(run.finishedAt ?? run.requestedAt)} EAT`;
  if (run.status === 'done') {
    return {
      tone: 'success',
      title: `Line test finished at ${at}`,
      detail: describeLineTestOutcome(run),
    };
  }
  // Whatever the CPE did measure before it stopped is worth saying: a download
  // figure with no upload is a different fault from nothing at all.
  const partial = [
    run.downBps !== null ? `the download measured ${formatBitRate(run.downBps)}` : null,
    run.upBps !== null ? `the upload measured ${formatBitRate(run.upBps)}` : null,
  ].filter(Boolean);
  // The CPE's reason often ends a sentence of its own; one full stop, not two.
  const reason = (run.error ?? 'the CPE gave no reason').trim().replace(/\.+$/, '');
  return {
    tone: run.status === 'timed_out' ? 'warn' : 'danger',
    title: `Line test ${run.status === 'timed_out' ? 'timed out' : 'failed'} at ${at}`,
    // No "run it again": for a CPE with no diagnostics object, it never helps.
    detail: `${reason}${partial.length ? ` — ${partial.join('; ')} before it stopped` : ''}. Nothing new was recorded as this ONU's speed.`,
  };
}
